import { InputRef } from 'antd'
import React, { useCallback, useRef, useState } from 'react'
import {
  SearchExpandableGroup,
  TDebouncedSearchExpandableGroupProps,
} from 'src/ui/DebouncedSearchExpandableGroup'
import SearchToggler, { TSearchTogglerProps } from 'src/ui/SearchToggler'

type TArguments = {
  inputs: TDebouncedSearchExpandableGroupProps['inputs']
  defaultValue?: TDebouncedSearchExpandableGroupProps['defaultValue']
  allowClear?: boolean
  persistentKey?: string
  searchTogglerProps?: Partial<TSearchTogglerProps>
}

export function useDebouncedExpandedSearchGroup<
  TSearch extends Record<string, $TSFixMe>
>({
  inputs,
  defaultValue = {},
  allowClear = true,
  persistentKey,
  searchTogglerProps,
}: TArguments) {
  const [search, setSearch] = useState<Partial<TSearch>>(
    defaultValue as TSearch
  )
  const firstInputRef = useRef<InputRef>()

  const focusSearchInput = useCallback((val: boolean) => {
    if (val) {
      if (firstInputRef.current) {
        firstInputRef.current.focus()
      }
    }
  }, [])
  const jsx = (
    <SearchToggler
      onVisibleChange={focusSearchInput}
      initOpen
      {...searchTogglerProps}
    >
      <SearchExpandableGroup<Partial<TSearch>>
        persistentKey={persistentKey}
        defaultValue={search}
        inputs={inputs.map((input, index) => ({
          ...input,
          getRef:
            index === 0
              ? (v: InputRef) => {
                  firstInputRef.current = v
                }
              : undefined,
        }))}
        inputPropsAll={{
          className: 'wh-ant-input variant--soft',
          allowClear,
        }}
        withDebouncedConfig={{
          onDebouncedChange: (val) => setSearch(val as Partial<TSearch>),
        }}
      />
    </SearchToggler>
  )

  return {
    jsx,
    search,
  }
}
